import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authAPI } from '../utils/api'
import Toast from '../components/Toast'
import Header from '../components/Header'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faLock, faRightToBracket, faIdBadge, faStar } from '@fortawesome/free-solid-svg-icons'

function Login({ onLogin }) {
  const [studentId, setStudentId] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await authAPI.login(studentId, password)
      const { token, user, requiresPasswordChange } = response.data

      onLogin(user, token)

      if (requiresPasswordChange) {
        navigate('/change-password')
      } else {
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <Header user={null} />
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.22),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(168,85,247,0.18),_transparent_28%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#ffffff_100%)] flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur rounded-3xl shadow-2xl p-8 w-full max-w-lg border border-white/60">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-lg">
              <FontAwesomeIcon icon={faStar} />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900 leading-none">
                SCiUS KKU 15
              </h1>
              <p className="text-sm text-gray-500">Pre-Camp Point System</p>
            </div>
          </div>

          <div className="mb-8 rounded-2xl bg-slate-900 text-white p-4 flex items-start gap-3">
            <FontAwesomeIcon icon={faIdBadge} className="mt-1 text-blue-300" />
            <div>
              <p className="font-semibold">First login uses your Student ID as the password</p>
              <p className="text-sm text-slate-300">After that you will be forced to change it once.</p>
            </div>
          </div>

          {error && (
            <Toast message={error} type="error" onClose={() => setError('')} />
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student ID
              </label>
              <input
                type="text"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                placeholder="e.g., 684888"
                className="input"
                disabled={loading}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••"
                  className="input pr-10"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-600 hover:text-gray-900"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                <FontAwesomeIcon icon={faLock} className="mr-1" /> First login: use your Student ID as password
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <FontAwesomeIcon icon={faRightToBracket} />
                {loading ? 'Logging in...' : 'Login'}
              </span>
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded">
            <p className="text-xs text-gray-600">
              <strong>Example Login:</strong>
              <br />
              ID: 684888 (Student ID)
              <br />
              Password: 684888 (first login)
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
